import { useState, useEffect, useCallback } from 'react';
import api from '../api';

// Fallback hardcoded statuses (used if API not yet seeded)
export const DEFAULT_OUT = [
  { key:'draft',                    label:'Draft',                       color:'gray',   enabled:true  },
  { key:'quotation_not_sent',       label:'Scheduled — Upcoming',        color:'purple', enabled:true  },
  { key:'quotation',                label:'Quotation (Sent)',             color:'cyan',   enabled:true  },
  { key:'to_invoice',               label:'To Invoice',                  color:'yellow', enabled:true  },
  { key:'created_not_sent',         label:'Created (Not Sent)',           color:'gray',   enabled:true  },
  { key:'sent_for_payment',         label:'Sent for Payment',            color:'blue',   enabled:true  },
  { key:'invoiced_within_terms',    label:'Invoiced',                    color:'blue',   enabled:true  },
  { key:'paid_in_part',             label:'Part Paid',                   color:'orange', enabled:true  },
  { key:'paid_in_full',             label:'Paid in Full',                color:'green',  enabled:true  },
  { key:'overdue',                  label:'Overdue',                     color:'red',    enabled:true  },
  { key:'very_overdue',             label:'Very Overdue',                color:'red',    enabled:true  },
  { key:'written_off',              label:'Written Off',                 color:'gray',   enabled:true  },
  { key:'send_for_debt_collection', label:'Debt Collection',             color:'red',    enabled:true  },
  { key:'receipt',                  label:'Receipt',                     color:'green',  enabled:true  },
  { key:'opportunity_value',        label:'Opportunity Value',           color:'orange', enabled:true  },
  { key:'po_requested',             label:'PO Requested',                color:'purple', enabled:true  },
  { key:'po_issued',                label:'PO Issued',                   color:'purple', enabled:true  },
  { key:'credited',                 label:'Credited',                    color:'teal',   enabled:true  },
  { key:'obsolete',                 label:'Obsolete',                    color:'gray',   enabled:true  },
  { key:'superseded_quote',         label:'Superseded Quote',            color:'orange', enabled:true  },
  { key:'deleted',                  label:'Deleted',                     color:'red',    enabled:true  },
  { key:'future_contractual',       label:'Scheduled — Upcoming',        color:'purple', enabled:false },
];

export const DEFAULT_IN = [
  { key:'recv_do_not_pay',        label:'Invoice Recv — Do NOT Pay Yet',   color:'red',    enabled:true },
  { key:'recv_pay_within_terms',  label:'Invoice Recv — PAY WITHIN TERMS', color:'orange', enabled:true },
  { key:'recv_assign_po',         label:'Invoice Recv — Assign PO',        color:'purple', enabled:true },
  { key:'recv_refused',           label:'Invoice Recv — REFUSED',          color:'red',    enabled:true },
  { key:'recv_overdue',           label:'Invoice Recv — OVERDUE',          color:'red',    enabled:true },
  { key:'recv_receipt',           label:'Receipt for Payment',             color:'green',  enabled:true },
  { key:'recv_credited',          label:'Credited by Supplier',            color:'teal',   enabled:true },
  { key:'paid_in_full',           label:'Paid in Full',                    color:'green',  enabled:true },
  { key:'quotation',              label:'Quotation (Sent)',                color:'cyan',   enabled:true },
  { key:'po_requested',           label:'PO Requested',                   color:'purple', enabled:true },
  { key:'po_issued',              label:'PO Issued',                      color:'purple', enabled:true },
  { key:'credited',               label:'Credited',                       color:'teal',   enabled:true },
  { key:'obsolete',               label:'Obsolete',                       color:'gray',   enabled:true },
  { key:'superseded_quote',       label:'Superseded Quote',               color:'orange', enabled:true },
  { key:'deleted',                label:'Deleted',                        color:'red',    enabled:true },
];

// Convert array config to {key: {label, color}} map (only enabled ones)
export function statusesToMap(arr) {
  const map = {};
  if (!arr) return map;
  arr.filter(s => s.enabled !== false).forEach(s => { map[s.key] = { label: s.label, color: s.color }; });
  return map;
}

// Cached globally so all components share the same fetch
let _cache = { out: null, in: null };
let _listeners = [];
function notifyListeners() { _listeners.forEach(fn => fn()); }

export async function fetchStatusConfig(direction) {
  try {
    const { data } = await api.get(`/settings/invoice-statuses/${direction}`);
    if (data && data.length > 0) {
      _cache[direction] = data;
      notifyListeners();
      return data;
    }
  } catch(e) {}
  _cache[direction] = direction === 'out' ? DEFAULT_OUT : DEFAULT_IN;
  notifyListeners();
  return _cache[direction];
}

export async function saveStatusConfig(direction, statuses) {
  await api.put(`/settings/invoice-statuses/${direction}`, statuses);
  _cache[direction] = statuses;
  notifyListeners();
}

export function useInvoiceStatuses() {
  const [outStatuses, setOutStatuses] = useState(_cache.out || DEFAULT_OUT);
  const [inStatuses, setInStatuses]   = useState(_cache.in  || DEFAULT_IN);
  const [loaded, setLoaded] = useState(!!(_cache.out && _cache.in));

  const refresh = useCallback(() => {
    if (_cache.out) setOutStatuses([..._cache.out]);
    if (_cache.in)  setInStatuses([..._cache.in]);
  }, []);

  useEffect(() => {
    _listeners.push(refresh);
    return () => { _listeners = _listeners.filter(fn => fn !== refresh); };
  }, [refresh]);

  useEffect(() => {
    if (!loaded) {
      Promise.all([fetchStatusConfig('out'), fetchStatusConfig('in')]).then(([out, inn]) => {
        setOutStatuses(out); setInStatuses(inn); setLoaded(true);
      });
    }
  }, [loaded]);

  const outMap = statusesToMap(outStatuses);
  const inMap  = statusesToMap(inStatuses);

  return { outStatuses, inStatuses, outMap, inMap, loaded };
}
